// Ammo ladder (UX §6.3): Round → Strip → Clip → Magazine → Drum → Ammo Box → Ammo Can → Pallet.
// A Round is the Loader's product: a copper-toned casing (Casing) with a lead tip (Slug).

import type { Swatch } from '../palette.ts';
import type { Part } from '../passes.ts';
import { box } from '../shapes.ts';
import type { ItemKit } from './kit.ts';

function roundParts(k: ItemKit, cx: number, top: number, w: number, h: number): Part[] {
  const cas = k.sw('material.casing');
  const tipH = h * 0.42;
  const L = k.X(cx - w / 2);
  const R = k.X(cx + w / 2);
  const T = k.Y(top);
  const B = k.Y(top + tipH + h * 0.04);
  const C = k.X(cx);
  const tip: Part = {
    path: (c) => {
      c.moveTo(L, B);
      c.bezierCurveTo(L, T + (B - T) * 0.35, C - (R - L) * 0.22, T, C, T);
      c.bezierCurveTo(C + (R - L) * 0.22, T, R, T + (B - T) * 0.35, R, B);
      c.closePath();
    },
    sw: k.sw('material.lead'),
    box: box(L, T, R - L, B - T),
  };
  const body = k.rect('material.casing', cx - w / 2, top + tipH, w, h - tipH, w * 0.1, {
    detail: () => {
      k.stroke(
        [
          [cx - w * 0.4, top + h * 0.86],
          [cx + w * 0.4, top + h * 0.86],
        ],
        cas.outline,
        k.ow * 0.45,
      );
    },
  });
  return [tip, body];
}

function band(k: ItemKit, fx: number, fy: number, fw: number, fh: number): void {
  k.fillRect(fx, fy, fw, fh, 0, k.pal.hex(k.pal.tier(k.tier)));
}

function flat(sw: Swatch, key: 'light' | 'shade'): Swatch {
  return { ...sw, base: sw[key] };
}

function boxParts(
  k: ItemKit,
  tok: string,
  x: number,
  y: number,
  w: number,
  h: number,
  lid: number,
): Part[] {
  const sw = k.sw(tok);
  const top = k.poly(
    tok,
    [
      [x + w * 0.1, y],
      [x + w * 0.9, y],
      [x + w, y + lid],
      [x, y + lid],
    ],
    0.02,
    { sw: flat(sw, 'light'), flat: true },
  );
  const front = k.rect(tok, x, y + lid, w, h - lid, 0.04);
  return [top, front];
}

export function ammoLadder(k: ItemKit): Part[] {
  switch (k.tier) {
    case 1:
      return roundParts(k, 0.5, 0, 0.4, 1);
    case 2: {
      const rounds = [0.2, 0.5, 0.8].flatMap((cx) => roundParts(k, cx, 0.02, 0.28, 0.84));
      const clip = k.rect('material.steel', 0.02, 0.76, 0.96, 0.2, 0.05, {
        detail: () => band(k, 0.4, 0.8, 0.2, 0.12),
      });
      return [...rounds, clip];
    }
    case 3: {
      const rounds = [0.2, 0.4, 0.6, 0.8].flatMap((cx) => roundParts(k, cx, 0, 0.2, 0.62));
      const steel = k.sw('material.steel');
      const body = k.rect('material.steel', 0, 0.38, 1, 0.6, 0.1, {
        detail: () => {
          for (const fx of [0.3, 0.5, 0.7]) {
            k.stroke(
              [
                [fx, 0.5],
                [fx, 0.84],
              ],
              steel.shade,
              k.ow * 0.6,
            );
          }
          band(k, 0, 0.58, 1, 0.12);
        },
      });
      return [...rounds, body];
    }
    case 4: {
      const tip = roundParts(k, 0.52, 0, 0.26, 0.5).slice(0, 1);
      const sw = k.sw('mach.front');
      const body = k.poly(
        'mach.front',
        [
          [0.28, 0.16],
          [0.76, 0.16],
          [0.84, 0.86],
          [0.34, 0.9],
        ],
        0.06,
        {
          detail: () => {
            for (const fy of [0.34, 0.48, 0.62]) k.fillRect(0.46, fy, 0.16, 0.06, 0.03, sw.outline);
            band(k, 0.3, 0.22, 0.46, 0.06);
          },
        },
      );
      const plate = k.rect('mach.top', 0.26, 0.84, 0.64, 0.14, 0.04);
      return [...tip, body, plate];
    }
    case 5: {
      const neck = k.rect('mach.front', 0.38, 0.06, 0.24, 0.2, 0.04);
      const tip = roundParts(k, 0.5, 0, 0.18, 0.3).slice(0, 1);
      const steel = k.sw('material.steel');
      const drum = k.ell('material.steel', 0.5, 0.6, 0.44, 0.4, {
        detail: (c) => {
          c.beginPath();
          c.ellipse(k.X(0.5), k.Y(0.6), k.S(0.3), k.S(0.27), 0, 0, Math.PI * 2);
          c.strokeStyle = k.pal.hex(k.pal.tier(k.tier));
          c.lineWidth = k.S(0.07);
          c.stroke();
          k.fillEll(0.5, 0.6, 0.13, 0.12, k.pal.hex('mach.front'));
          k.fillEll(0.5, 0.6, 0.05, 0.045, steel.hi);
          for (let i = 0; i < 6; i++) {
            const a = (i / 6) * Math.PI * 2 + 0.3;
            k.fillEll(
              0.5 + Math.cos(a) * 0.38,
              0.6 + Math.sin(a) * 0.34,
              0.025,
              0.025,
              steel.outline,
            );
          }
        },
      });
      return [...tip, neck, drum];
    }
    case 6: {
      const [top, front] = boxParts(k, 'ui.buyDark', 0, 0.2, 1, 0.78, 0.2);
      const handle = k.rect('mach.front', 0.34, 0.06, 0.32, 0.12, 0.05);
      const cream = k.pal.hex('ui.cream2');
      const deco: Part[] = front
        ? [
            {
              ...front,
              detail: () => {
                band(k, 0, 0.5, 1, 0.1);
                // stencilled bullet
                k.fillRect(0.44, 0.66, 0.12, 0.24, 0.02, cream);
                k.fillEll(0.5, 0.66, 0.06, 0.08, cream);
              },
            },
          ]
        : [];
      return [handle, ...(top ? [top] : []), ...deco];
    }
    case 7: {
      const steel = k.sw('mach.top');
      const handle = k.rect('mach.front', 0.3, 0, 0.4, 0.14, 0.06);
      const body = k.rect('mach.top', 0.08, 0.14, 0.84, 0.84, 0.08, {
        detail: () => {
          k.fillRect(0.08, 0.14, 0.84, 0.14, 0.04, steel.shade);
          band(k, 0.08, 0.5, 0.84, 0.12);
          k.fillRect(0.74, 0.3, 0.1, 0.14, 0.02, k.pal.hex('mach.front'));
          k.stroke(
            [
              [0.2, 0.74],
              [0.62, 0.74],
            ],
            steel.shade,
            k.ow * 0.6,
          );
        },
      });
      return [handle, body];
    }
    default: {
      const wood = k.sw('frame.wood');
      const boxesL = boxParts(k, 'ui.buyDark', 0.02, 0.3, 0.5, 0.48, 0.12);
      const boxesR = boxParts(k, 'ui.buyDark', 0.48, 0.3, 0.5, 0.48, 0.12);
      const can = k.rect('mach.top', 0.26, 0.02, 0.46, 0.32, 0.06, {
        detail: () => band(k, 0.26, 0.14, 0.46, 0.08),
      });
      const pallet = k.rect('frame.wood', 0, 0.76, 1, 0.22, 0.04, {
        detail: () => {
          for (const fx of [0.08, 0.44, 0.8]) k.fillRect(fx, 0.88, 0.12, 0.1, 0.01, wood.outline);
          band(k, 0.02, 0.62, 0.96, 0.06);
        },
      });
      return [...boxesL, ...boxesR, can, pallet];
    }
  }
}
